"use client";

import { useEffect, useState } from "react";
import { Progress } from "@/components/ui/progress";
import {
  getPopularRoomStyles,
  getPopularRoomTypes,
  type PopularItem,
} from "@/services/statistics.service";

interface RoomVisualizerPopularProps {
  type?: "styles" | "types";
}

export function RoomVisualizerPopular({
  type = "styles",
}: RoomVisualizerPopularProps) {
  const [data, setData] = useState<PopularItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const endDate = new Date();
        const startDate = new Date();
        startDate.setMonth(startDate.getMonth() - 1);

        const response =
          type === "styles"
            ? await getPopularRoomStyles(
                startDate.toISOString(),
                endDate.toISOString(),
                10
              )
            : await getPopularRoomTypes(
                startDate.toISOString(),
                endDate.toISOString(),
                10
              );

        if (response.success && response.data) {
          setData(response.data);
        }
        setError(null);
      } catch (err) {
        console.error(`Failed to fetch popular room ${type}:`, err);
        setError(`Failed to load popular room ${type}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [type]);

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="h-4 w-32 bg-muted animate-pulse rounded" />
              <div className="h-4 w-20 bg-muted animate-pulse rounded" />
            </div>
            <div className="h-2 w-full bg-muted animate-pulse rounded" />
          </div>
        ))}
      </div>
    );
  }

  if (error || data.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-sm text-muted-foreground">
          {error || `No popular room ${type} data available`}
        </p>
      </div>
    );
  }

  const formatName = (name: string) => {
    return name
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getImageForItem = (name: string) => {
    const imageMap: Record<string, string> = {
      // Room styles
      modern: "🏢",
      minimalist: "⬜",
      scandinavian: "🌲",
      industrial: "🏭",
      bohemian: "🌺",
      contemporary: "🎨",
      traditional: "🏛️",
      rustic: "🪵",
      coastal: "🌊",
      farmhouse: "🏡",
      // Room types
      "living-room": "🛋️",
      bedroom: "🛏️",
      kitchen: "🍳",
      bathroom: "🚿",
      "dining-room": "🍽️",
      office: "💼",
      "kids-room": "🧸",
      nursery: "👶",
      "home-office": "🖥️",
      "guest-room": "🚪",
    };

    return imageMap[name] || "🏠";
  };

  return (
    <div className="space-y-4">
      {data.map((item) => (
        <div key={item.name} className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-md bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center text-lg">
                {getImageForItem(item.name)}
              </div>
              <span className="font-medium">{formatName(item.name)}</span>
            </div>
            <span className="text-sm text-muted-foreground">
              {item.count} {type === "styles" ? "rooms" : "visualizations"}
            </span>
          </div>
          <Progress value={item.percentage} className="h-2" />
          <p className="text-xs text-muted-foreground text-right">
            {item.percentage}% of total
          </p>
        </div>
      ))}
    </div>
  );
}
